import { useState } from 'react';
import {
  RiAddLine,
  RiDeleteBin6Line,
  RiExchangeLine,
  RiEyeLine,
  RiMapPinLine,
  RiRouteLine,
  RiTimeLine,
} from 'react-icons/ri';
import type { IconType } from 'react-icons';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { useStore } from '../../state/store';
import { DRAG_MIME, type DragPayload } from '../../canvas/drag';
import { TIMELINE_KIND_LABEL, type TimelineKind } from '../../state/domain';
import { cn, formatDate } from '@/lib/utils';

const KIND_META: Record<TimelineKind, { icon: IconType; color: string }> = {
  event: { icon: RiTimeLine, color: 'var(--color-cord)' },
  communication: { icon: RiExchangeLine, color: 'var(--color-lead)' },
  movement: { icon: RiRouteLine, color: 'var(--color-assess)' },
  transaction: { icon: RiExchangeLine, color: 'var(--color-evidence)' },
  observation: { icon: RiEyeLine, color: 'var(--color-primary)' },
};

function localInput(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export function TimelinePanel() {
  const timeline = useStore(state => state.timeline);
  const activeTargetId = useStore(state => state.activeTargetId);
  const addTimeline = useStore(state => state.addTimeline);
  const deleteTimeline = useStore(state => state.deleteTimeline);

  const [form, setForm] = useState({
    kind: 'event' as TimelineKind,
    title: '',
    occurredAt: localInput(new Date()),
    location: '',
    description: '',
  });

  return (
    <div className="flex flex-col gap-2.5 p-3">
      <div className="flex items-center justify-between">
        <span className="label-caps">事件时间线</span>
        <Badge variant="muted">{timeline.length} 条</Badge>
      </div>

      <details className="rounded-[var(--radius)] border border-border bg-card p-2.5" open={timeline.length === 0}>
        <summary className="cursor-pointer text-[11px] text-muted-foreground">
          <span className="inline-flex items-center gap-1">
            <RiAddLine className="size-3.5" />
            登记事件
          </span>
        </summary>
        <div className="mt-2 flex flex-col gap-2">
          <div className="grid grid-cols-2 gap-1">
            {(Object.keys(TIMELINE_KIND_LABEL) as TimelineKind[]).map(kind => (
              <button
                key={kind}
                type="button"
                onClick={() => setForm({ ...form, kind })}
                className={cn(
                  'rounded-[var(--radius-sm)] border py-1 text-[10.5px]',
                  form.kind === kind ? 'border-primary bg-primary/8 text-primary' : 'border-border text-muted-foreground hover:bg-accent'
                )}
              >
                {TIMELINE_KIND_LABEL[kind]}
              </button>
            ))}
          </div>
          <Label>标题 *</Label>
          <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} />
          <Label>发生时间</Label>
          <Input
            type="datetime-local"
            value={form.occurredAt}
            onChange={e => setForm({ ...form, occurredAt: e.target.value })}
          />
          <Label>地点</Label>
          <Input value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} />
          <Label>描述</Label>
          <Textarea rows={2} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
          <Button
            size="sm"
            disabled={!form.title.trim()}
            onClick={async () => {
              await addTimeline({
                targetId: activeTargetId,
                kind: form.kind,
                title: form.title.trim(),
                occurredAt: new Date(form.occurredAt).toISOString(),
                location: form.location || null,
                description: form.description || null,
                participants: activeTargetId ? [activeTargetId] : [],
                confidence: 0.6,
              });
              setForm({ ...form, title: '', location: '', description: '' });
            }}
          >
            记入时间线
          </Button>
        </div>
      </details>

      <div className="relative pl-1">
        {timeline.length > 1 && (
          <div className="absolute bottom-2 left-[13px] top-2 w-px bg-border" aria-hidden />
        )}
        <div className="flex flex-col gap-2">
          {timeline.map(item => {
            const meta = KIND_META[item.kind] ?? KIND_META.event;
            const Icon = meta.icon;
            return (
              <div key={item.id} className="group relative flex gap-2.5">
                <div
                  className="z-[1] mt-1.5 grid size-6 shrink-0 place-items-center rounded-full border-2 border-card"
                  style={{ background: meta.color }}
                >
                  <Icon className="size-3 text-white" />
                </div>
                <div
                  className="min-w-0 flex-1 cursor-grab rounded-[var(--radius)] border border-border bg-card p-2.5 active:cursor-grabbing"
                  draggable
                  onDragStart={dragEvent => {
                    const payload: DragPayload = {
                      kind: 'timeline',
                      id: item.id,
                      title: `${TIMELINE_KIND_LABEL[item.kind] ?? item.kind}｜${item.title}`,
                      text: [
                        `时间：${formatDate(item.occurredAt)}`,
                        item.location ? `地点：${item.location}` : '',
                        item.description ?? '',
                      ]
                        .filter(Boolean)
                        .join('\n'),
                      color: 'orange',
                    };
                    dragEvent.dataTransfer.setData(DRAG_MIME, JSON.stringify(payload));
                    dragEvent.dataTransfer.effectAllowed = 'copy';
                  }}
                  title="拖到关联墙上"
                >
                  <div className="flex items-center gap-1.5">
                    <Badge variant="muted">{TIMELINE_KIND_LABEL[item.kind] ?? item.kind}</Badge>
                    <span className="ml-auto shrink-0 font-mono text-[9.5px] text-muted-foreground">
                      {formatDate(item.occurredAt)}
                    </span>
                    <Button
                      size="icon-sm"
                      variant="ghost"
                      className="opacity-0 transition-opacity group-hover:opacity-100"
                      onClick={() => deleteTimeline(item.id)}
                    >
                      <RiDeleteBin6Line className="size-3" />
                    </Button>
                  </div>
                  <div className="mt-1 text-[11.5px] font-medium">{item.title}</div>
                  {item.description && (
                    <p className="mt-0.5 text-[10.5px] leading-relaxed text-muted-foreground">{item.description}</p>
                  )}
                  {item.location && (
                    <div className="mt-1 inline-flex items-center gap-1 text-[10px] text-muted-foreground">
                      <RiMapPinLine className="size-3" />
                      {item.location}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {timeline.length === 0 && (
        <div className="rounded-[var(--radius)] border border-dashed border-border p-4 text-center text-[11.5px] text-muted-foreground">
          尚无事件记录。
        </div>
      )}
    </div>
  );
}